import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import useTour from "../../hooks/useTour";
import TourTooltip from "./TourTooltip";
import TourProgress from "./TourProgress";
import Button from "../buttons/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/pro-light-svg-icons";

const SPOTLIGHT_PADDING = 8;
const POSITION_POLL_MS = 100;

const TourOverlay = () => {
  const { t } = useTranslation();
  const {
    isActive,
    isTooltipVisible,
    showTooltip,
    restartTick,
    stepData,
    currentSection,
    currentStep,
  } = useTour();
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [targetReady, setTargetReady] = useState(false);
  const observerRef = useRef(null);

  const findTargets = useCallback(() => {
    if (!stepData?.target) return null;
    const matches = document.querySelectorAll(`[data-tour="${stepData.target}"]`);
    return matches.length ? Array.from(matches) : null;
  }, [stepData]);

  const updateSpotlight = useCallback(() => {
    const targets = findTargets();
    if (!targets?.length) {
      setSpotlightRect(null);
      setTargetReady(false);
      return;
    }
    const rects = targets.map((el) => el.getBoundingClientRect());
    const minTop = Math.min(...rects.map((r) => r.top));
    const minLeft = Math.min(...rects.map((r) => r.left));
    const maxRight = Math.max(...rects.map((r) => r.right));
    const maxBottom = Math.max(...rects.map((r) => r.bottom));

    setSpotlightRect({
      top: minTop - SPOTLIGHT_PADDING,
      left: minLeft - SPOTLIGHT_PADDING,
      width: maxRight - minLeft + SPOTLIGHT_PADDING * 2,
      height: maxBottom - minTop + SPOTLIGHT_PADDING * 2,
    });
    setTargetReady(true);
  }, [findTargets]);

  // Scroll target into view when step changes
  useEffect(() => {
    if (!isActive || !stepData?.target) {
      setSpotlightRect(null);
      setTargetReady(!stepData?.target);
      return;
    }
    setSpotlightRect(null);
    setTargetReady(false);

    const tryScrollAndHighlight = () => {
      const targets = findTargets();
      if (targets?.length) {
        targets[0].scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(updateSpotlight, 350);
        return true;
      }
      return false;
    };

    // Retry up to 2s for dynamically rendered elements
    if (!tryScrollAndHighlight()) {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (tryScrollAndHighlight() || attempts > 20) {
          clearInterval(interval);
          if (attempts > 20) setTargetReady(true); // fallback to centered
        }
      }, POSITION_POLL_MS);
      return () => clearInterval(interval);
    }
  }, [
    isActive,
    currentSection,
    currentStep,
    restartTick,
    stepData,
    findTargets,
    updateSpotlight,
  ]);

  // Keep spotlight aligned on scroll/resize
  useEffect(() => {
    if (!isActive || !stepData?.target) return;

    const handleUpdate = () => updateSpotlight();
    window.addEventListener("resize", handleUpdate);
    window.addEventListener("scroll", handleUpdate, true);

    const targets = findTargets();
    if (targets?.length && typeof ResizeObserver !== "undefined") {
      observerRef.current = new ResizeObserver(handleUpdate);
      targets.forEach((el) => observerRef.current.observe(el));
    }

    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate, true);
      observerRef.current?.disconnect();
    };
  }, [isActive, stepData, findTargets, updateSpotlight, restartTick]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        // handled by TourTooltip skip button
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isActive]);

  if (!isActive || !stepData) return null;

  const isCentered = !stepData.target || !spotlightRect;
  const isOverlayVisible = isTooltipVisible;

  return (
    <div className="tour-overlay">
      {/* Full overlay background for centered steps */}
      {isOverlayVisible && isCentered && <div className="tour-overlay-bg" />}

      {/* Block interactions outside spotlight */}
      {isOverlayVisible && !isCentered && spotlightRect && (
        <>
          <div
            className="tour-overlay-block"
            style={{
              top: 0,
              left: 0,
              width: "100%",
              height: Math.max(0, spotlightRect.top),
            }}
          />
          <div
            className="tour-overlay-block"
            style={{
              top: spotlightRect.top,
              left: 0,
              width: Math.max(0, spotlightRect.left),
              height: spotlightRect.height,
            }}
          />
          <div
            className="tour-overlay-block"
            style={{
              top: spotlightRect.top,
              left: spotlightRect.left + spotlightRect.width,
              width: `calc(100% - ${spotlightRect.left + spotlightRect.width}px)`,
              height: spotlightRect.height,
            }}
          />
          <div
            className="tour-overlay-block"
            style={{
              top: spotlightRect.top + spotlightRect.height,
              left: 0,
              width: "100%",
              height: `calc(100% - ${spotlightRect.top + spotlightRect.height}px)`,
            }}
          />
        </>
      )}

      {/* Spotlight cutout */}
      {isOverlayVisible && !isCentered && spotlightRect && (
        <div
          className="tour-spotlight"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
          }}
        />
      )}

      {/* Tooltip */}
      {isOverlayVisible && (targetReady || isCentered) && (
        <TourTooltip spotlightRect={spotlightRect} isCentered={isCentered} />
      )}

      {!isTooltipVisible && (
        <div className="tour-overlay__resume">
          <Button variant="info" className="gap-1" onClick={showTooltip}>
            <FontAwesomeIcon icon={faCircleQuestion} size="lg" />
            <span>{t("dashboard.tour.controls.resume")}</span>
          </Button>
        </div>
      )}

      {/* Progress indicator */}
      {isOverlayVisible && <TourProgress />}
    </div>
  );
};

export default TourOverlay;
